import { forwardRef, type HTMLAttributes } from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "@/components/ui/cn";

const cardVariants = cva(
  "rounded-xl border border-border bg-card text-card-foreground shadow-xs",
  {
    variants: {
      padding: {
        none: "p-0",
        md: "p-4",
        lg: "p-6",
        empty: "p-10",
      },
      interactive: {
        true: "cursor-pointer transition-all hover:border-neutral-300 hover:shadow-sm",
        false: "",
      },
    },
    defaultVariants: {
      padding: "md",
      interactive: false,
    },
  },
);

type CardProps = HTMLAttributes<HTMLDivElement> &
  VariantProps<typeof cardVariants> & {
    asChild?: boolean;
  };

export const Card = forwardRef<HTMLDivElement, CardProps>(
  ({ className, padding, interactive, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "div";
    return (
      <Comp
        ref={ref}
        className={cn(cardVariants({ padding, interactive }), className)}
        {...props}
      />
    );
  },
);
Card.displayName = "Card";

export type { CardProps };
